// @oasis-permission-inherits: /capcode (capcode:view)
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";

import { InfiniteQueryPagination } from "~/components/infinite-query-pagination";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  CostPerDayChart,
  CostPerPRChart,
  CostPerRunChart,
  ReviewDurationChart,
  RunCountsPerDayChart,
} from "~/modules/capcode-review/charts";
import { formatCost, formatRelativeTime } from "~/modules/capcode-review/format";
import {
  fetchCapCodeReviewMetricsQuery,
  fetchCapCodeReviewPRsQuery,
} from "~/modules/capcode-review/queries";

const METRICS_DAYS = 30;
const GRAFANA_URL =
  "https://nocap.grafana.net/d/capcode-review-performance/capcode-review-performance?orgId=1&from=now-24h&to=now&timezone=browser&refresh=1m";

const CHART_OPTIONS = [
  { value: "cost-per-pr", label: "Cost per PR" },
  { value: "cost-per-run", label: "Cost per recorded run" },
  { value: "cost-per-day", label: "Cost per day" },
  { value: "review-duration", label: "Duration per review attempt" },
  { value: "runs-per-day", label: "Runs per day" },
] as const;
type ChartKind = (typeof CHART_OPTIONS)[number]["value"];

const searchSchema = z.object({
  // Coerce + validate so a stale bookmark or hand-edited URL (e.g. ?page=abc)
  // degrades to "no page" instead of erroring the route.
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

export const Route = createFileRoute("/_dash/capcode/reviews/")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold">CapCode PR Reviews</h1>
        <p className="text-muted-foreground text-sm">
          Every PR reviewed by cap-code. Click a row to see all runs, verdicts, cost, and artifacts.
        </p>
      </div>

      <MetricsTab />

      <PRsTab />
    </div>
  );
}

function PRsTab() {
  const { page } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, error } =
    fetchCapCodeReviewPRsQuery.useInfiniteQuery({ params: {} });

  const totalPages = data?.pages.length ?? 0;
  const targetPageIndex = page ? page - 1 : 0;
  const [currentPageIndex, setCurrentPageIndex] = useState(targetPageIndex);

  useEffect(() => {
    if (targetPageIndex >= totalPages && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [targetPageIndex, totalPages, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (targetPageIndex < totalPages) {
      setCurrentPageIndex(targetPageIndex);
    } else if (!hasNextPage && totalPages > 0) {
      // Out-of-range ?page=N (stale bookmark / hand-edited URL): once all
      // pages are fetched and the target is still unreachable, clamp to the
      // last page instead of leaving a stale index that renders empty.
      setCurrentPageIndex(totalPages - 1);
    }
  }, [targetPageIndex, totalPages, hasNextPage]);

  const currentPageData = data?.pages[currentPageIndex];
  const prs = currentPageData?.prs ?? [];

  const onPreviousPage = () => {
    const previousPageIndex = Math.max(0, currentPageIndex - 1);
    setCurrentPageIndex(previousPageIndex);
    navigate({ search: { page: previousPageIndex + 1 } });
  };

  const onNextPage = async () => {
    const nextPageIndex = currentPageIndex + 1;
    setCurrentPageIndex(nextPageIndex);
    navigate({ search: { page: nextPageIndex + 1 } });
    if (nextPageIndex === totalPages) {
      await fetchNextPage();
    }
  };

  const onPageChange = (pageIndex: number) => {
    setCurrentPageIndex(pageIndex);
    navigate({ search: { page: pageIndex + 1 } });
  };

  return (
    <div className="flex flex-col gap-4">
      {error ? (
        <div className="text-red-600 text-sm">Failed to load reviews: {String(error)}</div>
      ) : null}

      {totalPages > 0 && !isLoading ? (
        <div className="flex justify-end">
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        </div>
      ) : null}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PR</TableHead>
              <TableHead>SHA</TableHead>
              <TableHead className="text-right">Runs</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="text-right">Tokens (in/out)</TableHead>
              <TableHead>Last model</TableHead>
              <TableHead>Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading && prs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground text-center">
                  No reviewed PRs yet.
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              prs.map((pr) => (
                <TableRow
                  key={pr.docId}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate({
                      to: "/capcode/reviews/$docId",
                      params: { docId: pr.docId },
                    })
                  }
                >
                  <TableCell>
                    {pr.repo ? (
                      <a
                        href={`https://github.com/${pr.repo}/pull/${pr.pr}`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {pr.repo}#{pr.pr} ↗
                      </a>
                    ) : (
                      <span className="font-medium">
                        {pr.repo}#{pr.pr}
                      </span>
                    )}
                    {pr.closed ? (
                      <Badge variant="secondary" className="ml-2">
                        closed
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="max-w-[280px] truncate font-mono text-xs">
                    {pr.sha ? pr.sha.slice(0, 10) : "—"}
                  </TableCell>
                  <TableCell className="text-right">{pr.cost.runCount}</TableCell>
                  <TableCell className="text-right">{formatCost(pr.cost.totalCostUsd)}</TableCell>
                  <TableCell className="text-right text-xs">
                    {pr.cost.totalInputTokens.toLocaleString()} /{" "}
                    {pr.cost.totalOutputTokens.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs">{pr.cost.lastModel ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {formatRelativeTime(pr.updatedAt)}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function MetricsTab() {
  const { data, isLoading, error } = fetchCapCodeReviewMetricsQuery.useQuery({
    params: { days: METRICS_DAYS },
  });

  const [chart, setChart] = useState<ChartKind>("cost-per-pr");

  function renderChart() {
    if (isLoading || !data) {
      return <Skeleton className="h-[264px] w-full" />;
    }
    if (chart === "cost-per-pr") {
      return <CostPerPRChart data={data.costPerPrPerDay} />;
    }
    if (chart === "cost-per-run") {
      return <CostPerRunChart data={data.costPerRunPerDay} />;
    }
    if (chart === "cost-per-day") {
      return <CostPerDayChart data={data.costPerDay} />;
    }
    if (chart === "review-duration") {
      return <ReviewDurationChart data={data.durationPerReviewPerDay} />;
    }
    return <RunCountsPerDayChart data={data.runsPerDay} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <Tabs value={chart} onValueChange={(value) => setChart(value as ChartKind)}>
          <TabsList>
            {CHART_OPTIONS.map((option) => (
              <TabsTrigger key={option.value} value={option.value}>
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Button asChild variant="outline" size="sm">
          <a href={GRAFANA_URL} target="_blank" rel="noreferrer">
            Grafana
            <ExternalLink className="size-4" />
          </a>
        </Button>
      </div>

      {error ? (
        <div className="text-red-600 text-sm">Failed to load metrics: {String(error)}</div>
      ) : null}

      {renderChart()}
    </div>
  );
}
